import React, { FC, useEffect, useState } from "react"
import Loading from "../../../ui/Loading"
import { KTitleSpan, KButton, KSpan, KTooltip } from "kahuna-base-react-components"
import { handleAuthorizedRequest, announcements as announcementsApi } from "../../../../apis"
import { AdminUserAnnouncement } from "../../../../types"
import { mapListToCamelCase } from "../../../../utility"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import { SetURLSearchParams } from "react-router-dom"
import SearchUserComponent from "../../../ui/components/SearchUserComponent"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import Pagination from "../../../ui/table/Pagination"

const columnWidthsWithUser: any = {
  ID: "5%",
  USER_ID: "7%",
  CREATED: "12%",
  TITLE: "18%",
  DESCRIPTION: "22%",
  READ: "8%",
  READ_ON: "10%",
  READ_AT: "12%"
}

const columnWidthsDefault: any = {
  ID: "5%",
  CREATED: "12%",
  TITLE: "20%",
  DESCRIPTION: "25%",
  READ: "8%",
  READ_ON: "10%",
  READ_AT: "12%"
}

type AdminAnnouncementWatchProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
}

const AdminAnnouncementWatch: FC<AdminAnnouncementWatchProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>(undefined)
  const [offset, setOffset] = useState(0)
  const [limit] = useState(20)
  const [announcementList, setAnnouncementList] = useState<AdminUserAnnouncement[]>([])
  const [totalCount, setTotalCount] = useState(0)

  const fetchAnnouncements = (currentOffset: number) => {
    setLoading(true)
    const userParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    handleAuthorizedRequest(
      async () => {
        const response = await announcementsApi(
          `/admin_user_announcements/?limit=${limit}&offset=${currentOffset}${userParam}`,
          "get"
        )
        if (response.status === 200) {
          const mapped = mapListToCamelCase(response.data.results)
          setAnnouncementList(mapped)
          setTotalCount(response.data.count || 0)
        }
        setLoading(false)
      },
      "Error fetching announcements",
      null
    )
  }

  useEffect(() => {
    setOffset(0)
    fetchAnnouncements(0)
  }, [selectedUser])

  const getReadOnLabel = (readOn: string | undefined) => {
    if (!readOn) return "-"
    return readOn === "mobile" ? "📱 Mobile" : "🌐 Web"
  }

  const renderAnnouncements = () => {
    if (announcementList.length === 0 && !loading) {
      return (
        <div className="flex justify-center items-center py-20">
          <KSpan text={selectedUser ? "No announcements found for this user" : "No announcements found"} color="#999" />
        </div>
      )
    }

    const columnWidths = selectedUser ? columnWidthsDefault : columnWidthsWithUser

    return (
      <div className="pt-4 pb-4 flex flex-col">
        <div className="flex pb-2 gap-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => (
            <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
              <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
            </div>
          ))}
        </div>
        {announcementList.map((item) => (
          <div
            key={`ann_${item.id}`}
            style={{ width: "100%" }}
            className="pt-5"
          >
            <div className="flex pt-2 gap-2" style={{ width: "100%", borderTop: "1px dashed grey" }}>
              <div style={{ width: columnWidths.ID }} className="flex items-center">
                <KSpan text={item.id?.toString() || "-"} />
              </div>
              {!selectedUser && (
                <div style={{ width: columnWidths.USER_ID }} className="flex items-center">
                  <KSpan text={item.userId?.toString() || "-"} />
                </div>
              )}
              <div style={{ width: columnWidths.CREATED }} className="flex items-center">
                <KSpan text={item.created?.substring(0, 19)?.replace("T", " ") || "-"} />
              </div>
              <div style={{ width: columnWidths.TITLE }} className="flex items-center">
                <KSpan text={item.title || "-"} />
              </div>
              <div
                style={{ width: columnWidths.DESCRIPTION, position: "relative" }}
                className="flex items-center"
              >
                <KTooltip
                  backgroundColor="#000"
                  position="bottom"
                  height="auto"
                  border="none"
                  padding="8px"
                  content={
                    <div
                      style={{ width: 600, maxHeight: 400, overflowY: "auto", whiteSpace: "pre-wrap", fontSize: 12, color: "#FFF" }}
                      dangerouslySetInnerHTML={{ __html: item.descriptionEn || item.descriptionTr || "-" }}
                    />
                  }
                >
                  <p className="text-xs text-gray-700 line-clamp-3 m-0 cursor-pointer">
                    {item.descriptionEn || item.descriptionTr || "-"}
                  </p>
                </KTooltip>
              </div>
              <div style={{ width: columnWidths.READ }} className="flex items-center">
                <div
                  className="px-2 py-1 rounded-md"
                  style={{
                    background: item.read ? "#E8F5E9" : "#FFF3E0",
                    border: `1px solid ${item.read ? "#A5D6A7" : "#FFB74D"}`
                  }}
                >
                  <KSpan
                    text={item.read ? "Read" : "Unread"}
                    fontSize={12}
                    color={item.read ? "#2E7D32" : "#E65100"}
                  />
                </div>
              </div>
              <div style={{ width: columnWidths.READ_ON }} className="flex items-center">
                <KSpan text={getReadOnLabel(item.readOn)} />
              </div>
              <div style={{ width: columnWidths.READ_AT }} className="flex items-center">
                <KSpan text={item.readAt?.substring(0, 19)?.replace("T", " ") || "-"} />
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTitleSpan fontSize={20} lineHeight="28px" text="Announcement Watch" />
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <KSpan text="User" />
            <SearchUserComponent className="relative" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          </div>
          <div>
            <KButton
              height="35px"
              text="Apply"
              background="black"
              textColor="white"
              onClick={() => {
                setOffset(0)
                fetchAnnouncements(0)
              }}
            />
          </div>
        </div>
      </div>
      {renderAnnouncements()}
      <Pagination
        hidden={totalCount <= limit}
        pageCount={Math.ceil(totalCount / limit)}
        currentPage={Math.floor(offset / limit) + 1}
        onClickPage={(page: number) => {
          const newOffset = (page - 1) * limit
          setOffset(newOffset)
          fetchAnnouncements(newOffset)
        }}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(AdminAnnouncementWatch)
